/**
 * Briques UI partagées du modèle de rôles (chips, badges, recherche, erreurs,
 * identités visuelles des portails et des natures) — consommées par l'atelier
 * des rôles, la fiche plateforme et le Portail Plateforme.
 */
import React from 'react';
import { Building2, ClipboardList, Handshake, Lock, LayoutGrid, LucideIcon, PenLine, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { OperationResponse } from 'api/atelier-roles';
import { portailLabel } from 'core/roles';

/** Identité visuelle de chaque portail : icône + couleurs (bandeau de carte, chip). */
const PORTAIL_STYLES: Record<string, { icon: LucideIcon; band: string; chip: string }> = {
  Distribution: { icon: Handshake, band: 'bg-blue-600', chip: 'bg-blue-50 text-blue-700' },
  Plateforme: { icon: Building2, band: 'bg-emerald-600', chip: 'bg-emerald-50 text-emerald-700' },
  Gestion: { icon: ClipboardList, band: 'bg-amber-600', chip: 'bg-amber-50 text-amber-800' }
};

export const portailStyle = (portail: string) =>
  PORTAIL_STYLES[portail] ?? { icon: LayoutGrid, band: 'bg-primary', chip: 'bg-gray-100 text-gray-700' };

/** Chip d'un portail : icône + « Portail X », aux couleurs du portail. */
export const PortailChip: React.FC<{ portail: string }> = ({ portail }) => {
  const style = portailStyle(portail);
  const Icon = style.icon;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        style.chip
      )}
    >
      <Icon className="h-3 w-3" />
      {portailLabel(portail)}
    </span>
  );
};

/** Couleurs par nature de PM (codes CRT/CIE/GST/MIA/PIM) — distinctes des
 * couleurs de portails pour ne pas mélanger les deux axes. */
const NATURE_STYLES: Record<string, string> = {
  CRT: 'bg-indigo-50 text-indigo-700',
  CIE: 'bg-purple-50 text-purple-700',
  MIA: 'bg-cyan-50 text-cyan-700',
  PIM: 'bg-orange-50 text-orange-700',
  GST: 'bg-slate-100 text-slate-600'
};

/** Chip d'une nature de PM : libellé serveur, couleur par code. */
export const NatureChip: React.FC<{ nature?: string | null; libelle?: string | null }> = ({
  nature,
  libelle
}) => {
  if (!nature && !libelle) {
    return <span className="text-gray-300">—</span>;
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        (nature && NATURE_STYLES[nature]) || 'bg-gray-100 text-gray-700'
      )}
    >
      {libelle ?? nature}
    </span>
  );
};

/** Chip neutre (portail, rôle porté par un profil…). */
export const Chip: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700',
      className
    )}
  >
    {label}
  </span>
);

/** Badge standard (verrouillée, pilotée par le code) / personnalisée (éditable). */
export const BriqueTypeBadge: React.FC<{ estStandard: boolean }> = ({ estStandard }) =>
  estStandard ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
      <Lock className="h-3 w-3" /> Standard
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
      <PenLine className="h-3 w-3" /> Personnalisée
    </span>
  );

/**
 * Exécute une opération de l'atelier et renvoie le message d'erreur à afficher
 * (null si succès). Les gardes serveur (brique standard, garde de nature,
 * porteurs actifs…) répondent en OperationResponse.Fail ou en erreur ABP.
 */
export const runOperation = async (
  operation: () => Promise<OperationResponse>
): Promise<string | null> => {
  try {
    const result = await operation();
    if (result.isSuccess === false) {
      return result.errorMessage ?? 'Opération refusée par le serveur.';
    }
    return null;
  } catch (err: unknown) {
    const response = (err as { response?: Response }).response;
    if (response) {
      try {
        const body = await response.json();
        const message = body?.error?.message ?? body?.errorMessage;
        if (typeof message === 'string' && message.length > 0) return message;
      } catch {
        // corps non JSON — on retombe sur le message générique
      }
      if (response.status === 403) return 'Accès refusé (permission Atelier des rôles requise).';
    }
    console.error("Erreur d'appel atelier-roles:", err);
    return "L'opération a échoué — réessayez.";
  }
};

/** Champ de recherche au style de la barre d'outils des tableaux génériques
 * (icône loupe à gauche, coins arrondis, focus primary). */
export const SearchInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder = 'Rechercher...', className }) => (
  <div className={clsx('relative', className)}>
    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
      <Search className="h-4 w-4 text-gray-400" aria-hidden="true" />
    </div>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 text-sm leading-5 text-gray-900 placeholder-gray-500 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25"
    />
  </div>
);

/** Bandeau d'erreur inline (gardes serveur affichées telles quelles). */
export const InlineError: React.FC<{ message: string | null }> = ({ message }) =>
  message ? (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  ) : null;
